<template>
  <v-container class="my-5">
    <v-card-title class="mb-4">
      <v-btn icon @click="goBack" class="mr-3">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="headline font-weight-bold">{{ tournamentName }}</span>
      <v-spacer></v-spacer>
    </v-card-title>

    <v-card class="elevation-0">
      <v-card-text>
        <v-row class="px-3 mb-2" align="center">
          <v-col cols="12" sm="6" md="4">
            <v-text-field
                v-model="search"
                label="Buscar por nombre"
                prepend-inner-icon="mdi-magnify"
                clearable
                hide-details
                :disabled="loading"
                @input="onSearchChange"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="8" class="text-sm-right">
            <v-btn
                color="red darken-1"
                dark
                class="mr-2"
                :loading="exportingPdf"
                :disabled="exportingExcel || loading"
                @click="exportPdf"
            >
              <v-icon small left>mdi-file-pdf-box</v-icon>
              Reporte PDF
            </v-btn>
            <v-btn
                color="green darken-1"
                dark
                :loading="exportingExcel"
                :disabled="exportingPdf || loading"
                @click="exportExcel"
            >
              <v-icon small left>mdi-file-excel</v-icon>
              Reporte Excel
            </v-btn>
          </v-col>
        </v-row>

        <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

        <div v-if="!loading && families.length === 0" class="text-center py-5">
          <p class="grey--text">No hay familias inscritas</p>
        </div>

        <v-data-table
            v-else
            :headers="headers"
            :items="families"
            :loading="loading"
            class="elevation-1"
            no-data-text="No hay familias registradas"
            item-key="id"
        >
          <template v-slot:item.total_members="{ item }">
            <v-chip small color="primary" text-color="white">{{ item.total_members }}</v-chip>
          </template>

          <template v-slot:item.created_at="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small class="mr-1" v-bind="attrs" v-on="on" @click="showDetails(item)">
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>Ver detalles</span>
            </v-tooltip>
          </template>
        </v-data-table>

        <!-- Paginación -->
        <v-layout v-if="totalCount > pageSize" row justify-center class="mt-4">
          <v-pagination
              v-model="currentPage"
              :length="Math.ceil(totalCount / pageSize)"
              :disabled="loading"
              @input="loadFamilies"
          ></v-pagination>
        </v-layout>
      </v-card-text>
    </v-card>

    <!-- Dialog de detalles -->
    <v-dialog v-model="dialogDetails" max-width="650px" scrollable>
      <v-card v-if="selectedFamily">
        <v-card-title class="primary--text">
          {{ selectedFamily.full_name }}
        </v-card-title>
        <v-card-subtitle>
          {{ selectedFamily.phone }} · {{ selectedFamily.email }}
        </v-card-subtitle>
        <v-divider></v-divider>

        <v-card-text class="pt-4">
          <v-row dense class="mb-2">
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text text-uppercase">Club</div>
              <div class="font-weight-medium">{{ selectedFamily.club || '-' }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text text-uppercase">Registrado</div>
              <div class="font-weight-medium">{{ formatDateTime(selectedFamily.created_at) }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text text-uppercase">Total integrantes</div>
              <div class="font-weight-medium">{{ selectedFamily.total_members }}</div>
            </v-col>
          </v-row>

          <div v-if="selectedFamily.children && selectedFamily.children.length" class="mb-4">
            <div class="font-weight-bold mb-1">Niños</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Edad</th>
                  <th>Género</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="child in selectedFamily.children" :key="child.id">
                  <td>{{ child.full_name }}</td>
                  <td>{{ child.age !== null && child.age !== undefined ? child.age : '-' }}</td>
                  <td>{{ genderLabel(child.gender) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <div v-if="selectedFamily.guests && selectedFamily.guests.length">
            <div class="font-weight-bold mb-1">Acompañantes</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Relación</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="guest in selectedFamily.guests" :key="guest.id">
                  <td>{{ guest.full_name }}</td>
                  <td>{{ guest.relationship || '-' }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <div
              v-if="(!selectedFamily.children || !selectedFamily.children.length) && (!selectedFamily.guests || !selectedFamily.guests.length)"
              class="grey--text"
          >
            Sin integrantes adicionales
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogDetails = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="5000" top>
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import JSPDF from 'jspdf'
import 'jspdf-autotable'
import kidsTournamentService from '@/services/kidsTournamentService'

export default {
  name: 'ClubHouseTournamentMembers',
  data () {
    return {
      tournamentName: '',
      search: '',
      loading: false,
      families: [],
      allFamilies: [],
      currentPage: 1,
      pageSize: 10,
      totalCount: 0,
      searchTimeout: null,
      exportingPdf: false,
      exportingExcel: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'error',
      dialogDetails: false,
      selectedFamily: null,
      // Tournament code para el torneo infantil-juvenil
      currentTournamentCode: 'kids_1788455854_h4RsKPKJwvM',
      headers: [
        { text: 'Titular', value: 'full_name' },
        { text: 'Teléfono', value: 'phone' },
        { text: 'Email', value: 'email' },
        { text: 'Club', value: 'club' },
        { text: 'Integrantes', value: 'total_members', align: 'center', sortable: false },
        { text: 'Registrado', value: 'created_at', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ]
    }
  },
  async mounted () {
    this.tournamentName = this.$route.params.tournamentName || 'Torneo'
    await this.loadFamilies()
  },
  methods: {
    async loadFamilies () {
      this.loading = true
      try {
        const options = {
          page: this.currentPage,
          page_size: this.pageSize,
          tournament_code: this.currentTournamentCode
        }

        if (this.search) {
          options.search = this.search
        }

        const response = await kidsTournamentService.getTournamentFamilies(options)
        // Manejo flexible de la estructura de respuesta
        let familiesList = []
        if (Array.isArray(response.results)) {
          familiesList = response.results
        } else if (response.results && Array.isArray(response.results.families)) {
          familiesList = response.results.families
        } else if (Array.isArray(response.families)) {
          familiesList = response.families
        }

        // Filtrar por tournament_code actual (por si acaso la API no lo hace)
        familiesList = familiesList.filter(member =>
          member.tournament_code === this.currentTournamentCode && !member.is_guest
        )

        this.families = familiesList
        this.totalCount = response.count || familiesList.length
      } catch (error) {
        console.error('Error loading families:', error)
        this.showMessage('Error al cargar los inscritos', 'error')
        this.families = []
      } finally {
        this.loading = false
      }
    },
    onSearchChange () {
      this.currentPage = 1
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadFamilies()
      }, 500)
    },
    goBack () {
      this.$router.go(-1)
    },
    showDetails (item) {
      this.selectedFamily = item
      this.dialogDetails = true
    },
    async fetchAllFamiliesForExport () {
      const pageSize = 100
      let page = 1
      let allFamilies = []
      let total = null

      do {
        const response = await kidsTournamentService.getTournamentFamilies({
          page,
          page_size: pageSize,
          tournament_code: this.currentTournamentCode,
          search: this.search || undefined
        })

        // Manejo flexible de la estructura de respuesta
        let familiesList = []
        if (Array.isArray(response.results)) {
          familiesList = response.results
        } else if (response.results && Array.isArray(response.results.families)) {
          familiesList = response.results.families
        } else if (Array.isArray(response.families)) {
          familiesList = response.families
        }

        // Filtrar por tournament_code actual y excluir invitados
        familiesList = familiesList.filter(member =>
          member.tournament_code === this.currentTournamentCode && !member.is_guest
        )

        allFamilies = allFamilies.concat(familiesList)
        total = response.count || 0
        page += 1
      } while (allFamilies.length < total)

      return allFamilies
    },
    buildExportRows (families) {
      const headers = [
        'Tipo', 'Nombre', 'Teléfono', 'Email', 'Club',
        'Integrantes', 'Edad', 'Género', 'Relación', 'Registrado'
      ]
      const blank = '-'

      const rows = []
      families.forEach(family => {
        rows.push([
          'Titular',
          family.full_name || blank,
          family.phone || blank,
          family.email || blank,
          family.club || blank,
          family.total_members,
          blank, blank, blank,
          this.formatDateTime(family.created_at)
        ])

        ;(family.children || []).forEach(child => {
          rows.push([
            'Niño',
            child.full_name || blank,
            blank, blank, blank, blank,
            child.age !== null && child.age !== undefined ? child.age : blank,
            this.genderLabel(child.gender),
            child.relationship || blank,
            blank
          ])
        })

        ;(family.guests || []).forEach(guest => {
          rows.push([
            'Acompañante',
            guest.full_name || blank,
            blank, blank, blank, blank, blank, blank,
            guest.relationship || blank,
            blank
          ])
        })
      })

      return { headers, rows }
    },
    async exportPdf () {
      this.exportingPdf = true
      try {
        const families = await this.fetchAllFamiliesForExport()
        if (!families.length) {
          this.showMessage('No hay familias para exportar', 'error')
          return
        }

        const { headers, rows } = this.buildExportRows(families)

        const doc = new JSPDF('landscape', 'pt', 'letter')
        doc.setFontSize(14)
        doc.text(`${this.tournamentName}`, 40, 40)

        doc.autoTable({
          startY: 60,
          head: [headers],
          body: rows,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [21, 101, 192] },
          didParseCell: (data) => {
            if (data.section === 'body' && data.row.raw[0] === 'Titular') {
              data.cell.styles.fontStyle = 'bold'
              data.cell.styles.fillColor = [235, 241, 250]
            }
          }
        })

        doc.save(`${this.slugify(this.tournamentName)}_${this.exportFileDate()}.pdf`)
        this.showMessage('Reporte PDF descargado correctamente', 'success')
      } catch (error) {
        console.error('Error exporting to PDF:', error)
        this.showMessage('Error al generar el reporte PDF', 'error')
      } finally {
        this.exportingPdf = false
      }
    },
    async exportExcel () {
      this.exportingExcel = true
      try {
        const families = await this.fetchAllFamiliesForExport()
        if (!families.length) {
          this.showMessage('No hay familias para exportar', 'error')
          return
        }

        const { headers, rows } = this.buildExportRows(families)

        const csvContent = [headers, ...rows]
          .map(row => row.map(cell => this.csvEscape(cell)).join(','))
          .join('\r\n')

        const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${this.slugify(this.tournamentName)}_${this.exportFileDate()}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.showMessage('Reporte Excel descargado correctamente', 'success')
      } catch (error) {
        console.error('Error exporting to Excel:', error)
        this.showMessage('Error al generar el reporte Excel', 'error')
      } finally {
        this.exportingExcel = false
      }
    },
    csvEscape (value) {
      const text = value === null || value === undefined ? '' : String(value)
      if (/[",\r\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`
      }
      return text
    },
    slugify (text) {
      return (text || 'torneo')
        .toString()
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .toLowerCase()
    },
    exportFileDate () {
      return new Date().toISOString().slice(0, 10)
    },
    showMessage (text, color) {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },
    genderLabel (gender) {
      if (gender === 'M') return 'Masculino'
      if (gender === 'F') return 'Femenino'
      return '-'
    },
    formatDateTime (datetime) {
      if (!datetime) return '-'
      return new Date(datetime).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.family-card {
  transition: all 0.3s ease;
  height: 100%;
}

.family-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.caption {
  word-break: break-word;
}
</style>
